// @flow
import React, {Component} from 'react';
import classNames from 'classnames/bind';
import {defineMessages, FormattedMessage, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import {FormValidator} from '../../../../utils/form/index';
import analytics from '../../../../analytics';
import {urlVerifier} from '../../../../utils/string';
import Discover from '../Discover/Discover';
import {Default, Mobile} from '../../../../utils/responsive';

import style from './HomeForm.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import laptop from '../../../../assets/img/home/how-it-works/laptop.png';

import type {FormValidationResult} from '../../../../utils/form';
import type {LocaleState} from '../../../locale/duck';

type Props = {
  +titleIntl?: string;
  +goToRedirect: (route: string) => void;
  +locale: LocaleState;
};

type State = {
  url: {
    value: string;
    errors?: string[];
  };
  isBrowser: boolean;
};

const messages = defineMessages({
  titleFirst: {
    id: 'newOrder.scrap.title.first',
  },
  titleSecond: {
    id: 'newOrder.scrap.title.second',
  },
});

const videoUrls = {
  CO: 'https://www.youtube.com/embed/41Vug0RLHiU?rel=&controls=0&showinfo=0&autoplay=1',
  PE: 'https://www.youtube.com/embed/41Vug0RLHiU?rel=&controls=0&showinfo=0&autoplay=1',
  US: 'https://www.youtube.com/embed/41Vug0RLHiU?rel=&controls=0&showinfo=0&autoplay=1',
};

export default class HomeForm extends Component<Props, State> {
  formValidator: FormValidator;
  state = {
    url: {
      value: '',
    },
    isBrowser: false,
  };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      url: {
        required: 'required',
      },
    });
  }

  componentDidMount() {
    this.setState({isBrowser: true});
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const {name} = e.target;
      this.setState({[name]: {value: this.state[name].value}});
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const {name, value} = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0] === 'urlFormat')) {
        this.setState({[name]: {value, errors}});
      } else {
        this.setState({[name]: {value}});
      }
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const {name, value} = e.target;
      this.setState({[name]: {value}});
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const {goToRedirect, locale} = this.props;
    const {url} = this.state;
    const errors = this.formValidator.validateForm({
      url: url.value,
    });

    analytics.fireCustomEvent('Home Main Button Clicked', {
      category: 'Home Main Button',
      action: 'Clicked',
      user_type: 'purchaser',
      user_referrer: window.localStorage.getItem('userReferrer') || '',
      landing_url: window.localStorage.getItem('landingUrl') || '',
      url: url.value || '',
      domain_country: locale.domainCountry,
    });

    if (errors.length) {
      errors.forEach(
        (error: FormValidationResult) =>
          this.setState(
            (state: State) => ({
                [error.field]: {
                  ...state[error.field],
                  errors: error.errors,
                },
              }
            )),
      );
    } else {
      if (urlVerifier(url.value) || url.value.startsWith('http')) {
        goToRedirect(`/new-order/product?initialQuery=${url.value}`);
      } else {
        goToRedirect(`/new-order/search?name=${url.value}`);
      }
    }
    return false;
  }

  fireHowItWorksEvent = () => {
    const {locale} = this.props;
    const {url} = this.state;

    analytics.fireCustomEvent('Home How It Works Button Clicked', {
      category: 'Home How It Works Button',
      action: 'Clicked',
      user_type: 'purchaser',
      user_referrer: window.localStorage.getItem('userReferrer') || '',
      landing_url: window.localStorage.getItem('landingUrl') || '',
      url: url.value || '',
      domain_country: locale.domainCountry,
    });
  };

  getUrlLabelIntl(): string {
    const {locale} = this.props;
    if (locale.domainCountry === 'CO') {
      return 'newOrder.scrap.url.label.CO';
    } else return 'newOrder.scrap.url.label';
  }

  render() {
    const {url, isBrowser} = this.state;
    const {titleIntl, locale} = this.props;
    const urlError = url && url.errors && FormValidator.buildErrorLabel(url.errors, {
      required: 'newOrder.scrap.error.required',
      urlFormat: 'newOrder.scrap.error.format',
    });

    const title = !!titleIntl ? <h1 className={style.title}>{this.context.intl.formatMessage({id: titleIntl})}</h1> :
      <h1 className={classNames(style.title)}>
        {`${this.context.intl.formatMessage(messages.titleFirst)} `}
        {`${this.context.intl.formatMessage(messages.titleSecond)} `}
      </h1>;
    
    const playButton = <section className={style.discover}>
      <img className={style.laptop}
           src={laptop}
           alt="laptop"/>
      <section className={style.discoverWrap}>
        {isBrowser && <Discover url={videoUrls[locale.domainCountry || 'PE']}
                                color="lightBlue"
                                handleClick={this.fireHowItWorksEvent}/>}
      </section>
    </section>;
    
    return (
      <section className={classNames(style.homeFormWrap)}>
        <section className={classNames(style.homeForm)}>
          <form className={classNames(style.homeFormContent)}
                action="/new-order/product"
                onSubmit={this.handleSubmit.bind(this)}>
            {title}
            {/*hasDiscover &&
            <FormattedMessage id="newOrder.scrap.form.subtitle">{
              text => <p className={style.subtitle}>{text}</p>
            }</FormattedMessage>*/}
            {<section className={style.inputWrap}>
              <FormattedMessage id="newOrder.scrap.url.label">{
                txt => <label className={style.inputLabel}>{txt}</label>
              }</FormattedMessage>
              <FormattedMessage id="newOrder.scrap.url.placeholder">{
                txt => <input type="text"
                              name="url"
                              value={url.value}
                              className={classNames(inputStyle.input,
                                {[inputStyle.red]: url.errors && url.errors.length})}
                              onFocus={this.handleFocus.bind(this)}
                              onBlur={this.handleBlur.bind(this)}
                              onChange={this.handleChange.bind(this)}
                              placeholder={txt}
                              autoComplete="disabled"/>
              }</FormattedMessage>
              <FormattedMessage id="newOrder.scrap.button.quoteFree">{
                text => <button type="submit"
                                className={classNames(buttonStyle.btn,
                                  buttonStyle.lightBlue,
                                  style.button)}>{text}</button>
              }</FormattedMessage>
              {urlError}
            </section>}
            <Mobile>{playButton}</Mobile>
          </form>
          <Default>{playButton}</Default>
        </section>
      </section>
    );
  }
}

HomeForm.propTypes = {
  titleIntl: PropTypes.string,
  goToRedirect: PropTypes.func.isRequired,
  locale: PropTypes.object.isRequired,
};

HomeForm.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/home/components/HomeForm/HomeForm.jsx